'use client';

import { mutate, type Arguments } from 'swr';
import { swrKeys } from './config';

// Global timestamp for last optimistic update - prevents redundant refetches
// Realtime handlers should check this before triggering refetches
let lastOptimisticUpdateTime = 0;

export function markOptimisticUpdate() {
  lastOptimisticUpdateTime = Date.now();
}

export function getTimeSinceOptimisticUpdate(): number {
  return Date.now() - lastOptimisticUpdateTime;
}

export function shouldSkipRevalidation(thresholdMs: number = 2000): boolean {
  return getTimeSinceOptimisticUpdate() < thresholdMs;
}

// Inline dashboard key to avoid circular dependency with dashboard.ts
const dashboardKey = (businessId: string) => ['dashboard', businessId];

// Dashboard data shape (duplicated to avoid circular import)
interface DashboardDataShape {
  upcoming: { bookings: unknown[]; totalCount: number };
  due: { bookings: unknown[]; totalCount: number };
  services: unknown[];
  staff: unknown[];
  alerts: unknown[];
  earnings: { total: number; cash: number; card: number };
  timestamp?: number;
}

// SWR key type for our cache keys
type SWRKey = Arguments;

// ============================================================================
// Types
// ============================================================================

export type InvalidationType =
  | 'bookings'
  | 'earnings'
  | 'salesSummary'
  | 'payments'
  | 'customers'
  | 'alerts'
  | 'services'
  | 'staff'
  | 'inbox'
  | 'expenses'
  | 'inventory'
  | 'businessHours'
  | 'offers'
  | 'dashboard';

// Types that trigger dashboard invalidation
const DASHBOARD_TRIGGER_TYPES: InvalidationType[] = [
  'bookings',
  'earnings',
  'services',
  'staff',
  'alerts',
];

type PendingInvalidation = {
  businessId: string;
  types: Set<InvalidationType>;
  timer: NodeJS.Timeout | null;
};

// ============================================================================
// Batched Invalidation System
// ============================================================================

// Pending invalidations keyed by businessId
const pendingInvalidations = new Map<string, PendingInvalidation>();

// Batch window in milliseconds - invalidations within this window are batched
// Set to 0 for instant invalidation - optimistic updates handle UI already
const BATCH_WINDOW_MS = 0;

/**
 * Execute all pending invalidations for a business.
 * This is called after the batch window expires.
 */
function executeBatchedInvalidation(businessId: string) {
  const pending = pendingInvalidations.get(businessId);
  if (!pending) return;

  const { types } = pending;

  // Clear pending state
  pendingInvalidations.delete(businessId);

  // Get today's date for sales summary keys
  const today = new Date().toISOString().split('T')[0];

  // NOTE: We call mutate() WITHOUT setting data to undefined.
  // This triggers revalidation while keeping previous data visible,
  // enabling smooth animations (e.g., earnings counter increments smoothly).

  // Process each type
  if (types.has('bookings')) {
    // Invalidate all booking-related keys
    mutate(
      (key) => {
        if (!Array.isArray(key)) return false;
        return key[0] === 'bookings' && key.includes(businessId);
      }
    );
    // Also explicit common limits
    [5, 10, 20].forEach(limit => {
      mutate(swrKeys.upcomingBookings(businessId, limit));
      mutate(swrKeys.dueBookings(businessId, limit));
    });
  }

  if (types.has('earnings')) {
    mutate(swrKeys.todayEarnings(businessId));
    // Also invalidate staff-specific earnings (using matcher)
    mutate(
      (key) => {
        if (!Array.isArray(key)) return false;
        return key[0] === 'earnings' && key.includes(businessId);
      }
    );
  }

  if (types.has('salesSummary')) {
    // Use filter function to match all sales summary keys for this business
    // Keys are: ['sales', 'summary', businessId, range, date, staffKey]
    mutate(
      (key) => Array.isArray(key) && key[0] === 'sales' && key[1] === 'summary' && key[2] === businessId
    );
  }

  if (types.has('payments')) {
    mutate(
      (key) => Array.isArray(key) && key[0] === 'payments' && key.includes(businessId)
    );
  }

  if (types.has('customers')) {
    mutate(swrKeys.customers(businessId));
  }

  if (types.has('alerts')) {
    mutate(swrKeys.alerts(businessId));
  }

  if (types.has('services')) {
    mutate(swrKeys.services(businessId));
    mutate(swrKeys.allServices(businessId));
  }

  if (types.has('staff')) {
    mutate(swrKeys.staff(businessId));
    mutate(swrKeys.allStaff(businessId));
  }

  if (types.has('inbox')) {
    mutate(swrKeys.inboxMessages(businessId));
    mutate(swrKeys.unreadCount(businessId));
  }

  if (types.has('expenses')) {
    mutate(
      (key) => typeof key === 'string' && key.startsWith(`expenses-${businessId}`)
    );
  }

  if (types.has('inventory')) {
    mutate(
      (key) => Array.isArray(key) && key[0] === 'inventory' && key[1] === businessId
    );
  }

  if (types.has('businessHours')) {
    mutate(swrKeys.businessHours(businessId));
  }

  if (types.has('offers')) {
    mutate(
      (key) => {
        if (!Array.isArray(key)) return false;
        return key[0] === 'customerOffers' && key[1] === businessId;
      }
    );
  }

  // Dashboard invalidation - triggered by specific types or explicit 'dashboard' type
  const shouldInvalidateDashboard =
    types.has('dashboard') ||
    DASHBOARD_TRIGGER_TYPES.some(type => types.has(type));

  if (shouldInvalidateDashboard) {
    mutate(['dashboard', businessId]);
  }
}

/**
 * Batch multiple invalidation types into a single operation.
 * Invalidations within BATCH_WINDOW_MS are combined.
 *
 * @example
 * // Instead of:
 * invalidateBookings(businessId);
 * invalidateEarnings(businessId);
 * invalidateSalesSummary(businessId);
 *
 * // Use:
 * batchInvalidate(businessId, ['bookings', 'earnings', 'salesSummary']);
 */
export function batchInvalidate(
  businessId: string,
  types: InvalidationType | InvalidationType[]
) {
  const typesArray = Array.isArray(types) ? types : [types];

  let pending = pendingInvalidations.get(businessId);

  if (!pending) {
    pending = {
      businessId,
      types: new Set(),
      timer: null,
    };
    pendingInvalidations.set(businessId, pending);
  }

  // Add new types to pending set
  typesArray.forEach(type => pending!.types.add(type));

  // Clear existing timer if any
  if (pending.timer) {
    clearTimeout(pending.timer);
  }

  // Set new timer
  pending.timer = setTimeout(() => {
    executeBatchedInvalidation(businessId);
  }, BATCH_WINDOW_MS);
}

/**
 * Immediately execute all pending invalidations without waiting for batch window.
 * Use this when you need to ensure data is fresh before proceeding.
 */
export function flushInvalidations(businessId: string) {
  const pending = pendingInvalidations.get(businessId);
  if (pending?.timer) {
    clearTimeout(pending.timer);
    pending.timer = null;
  }
  executeBatchedInvalidation(businessId);
}

// ============================================================================
// Optimistic Update Utilities
// ============================================================================

type UpdaterFn<T> = (current: T | undefined) => T;

/**
 * Apply an optimistic update to the cache.
 * Updates the cache immediately without revalidation.
 *
 * @param key - SWR cache key
 * @param updater - Function that receives current data and returns new data
 * @returns Promise that resolves when cache is updated
 */
export async function optimisticUpdate<T>(
  key: SWRKey,
  updater: UpdaterFn<T>
): Promise<void> {
  await mutate(key, updater, { revalidate: false });
}

/**
 * Apply an optimistic update and schedule revalidation.
 * Use this when you want immediate UI update but also want to sync with server.
 *
 * @param key - SWR cache key
 * @param updater - Function that receives current data and returns new data
 * @param delayMs - Delay before revalidation (default: 500ms)
 */
export async function optimisticUpdateWithRevalidate<T>(
  key: SWRKey,
  updater: UpdaterFn<T>,
  delayMs: number = 500
): Promise<void> {
  // Apply optimistic update immediately
  await mutate(key, updater, { revalidate: false });

  // Schedule revalidation (without clearing data for smooth transitions)
  setTimeout(() => {
    mutate(key);
  }, delayMs);
}

/**
 * Update cache with mutation result data.
 * Use this to update the cache with the response from API mutation.
 *
 * @param key - SWR cache key
 * @param data - New data to set in cache
 * @param revalidate - Whether to revalidate after setting (default: false)
 */
export async function updateCacheWithResult<T>(
  key: SWRKey,
  data: T,
  revalidate: boolean = false
): Promise<void> {
  await mutate(key, data, { revalidate });
}

// ============================================================================
// Mutation Helpers for Common Operations
// ============================================================================

/**
 * Optimistically add a booking to the upcoming bookings list and dashboard.
 */
export function optimisticAddBooking(
  businessId: string,
  booking: unknown,
  limit: number = 5
) {
  markOptimisticUpdate(); // Mark timestamp to prevent redundant refetches
  const key = swrKeys.upcomingBookings(businessId, limit);

  // Update individual hook cache
  mutate(
    key,
    (current: { bookings: unknown[]; totalCount: number } | undefined) => {
      // Handle undefined or empty cache
      const existingBookings = current?.bookings ?? [];
      const existingCount = current?.totalCount ?? 0;

      // Sort by start_at and insert new booking
      const newBookings = [...existingBookings, booking].sort((a: any, b: any) => {
        return new Date(a.start_at).getTime() - new Date(b.start_at).getTime();
      });

      return {
        bookings: newBookings.slice(0, limit),
        totalCount: existingCount + 1,
      };
    },
    { revalidate: false }
  );

  // Also update dashboard cache for instant UI
  mutate(
    dashboardKey(businessId),
    (current: DashboardDataShape | undefined) => {
      if (!current) return current;

      const existingBookings = current.upcoming?.bookings ?? [];
      const existingCount = current.upcoming?.totalCount ?? 0;

      // Sort by start_at and insert new booking
      const newBookings = [...existingBookings, booking].sort((a: any, b: any) => {
        return new Date(a.start_at).getTime() - new Date(b.start_at).getTime();
      });

      return {
        ...current,
        upcoming: {
          bookings: newBookings.slice(0, 5) as DashboardDataShape['upcoming']['bookings'],
          totalCount: existingCount + 1,
        },
      };
    },
    { revalidate: false }
  );
}

/**
 * Optimistically add a booked time range to the barber availability cache.
 * This ensures that time slots are immediately shown as unavailable after creating a booking.
 */
export function optimisticAddBarberAvailability(
  businessId: string,
  staffId: string,
  date: string,
  startAt: string,
  endAt: string
) {
  const key = swrKeys.barberAvailability(businessId, staffId, date);

  mutate(
    key,
    (current: Array<{ start_at: string; end_at: string }> | undefined) => {
      const existingRanges = current ?? [];

      // Add the new booking range
      const newRange = { start_at: startAt, end_at: endAt };

      // Return sorted by start time
      return [...existingRanges, newRange].sort((a, b) => {
        return new Date(a.start_at).getTime() - new Date(b.start_at).getTime();
      });
    },
    { revalidate: false }
  );
}

/**
 * Optimistically remove a booking from the lists and dashboard.
 */
export function optimisticRemoveBooking(
  businessId: string,
  bookingId: string,
  limit: number = 5
) {
  markOptimisticUpdate(); // Mark timestamp to prevent redundant refetches
  const filterOut = (bookings: unknown[]) =>
    bookings.filter((b: any) => b.id !== bookingId);

  // Remove from upcoming
  mutate(
    swrKeys.upcomingBookings(businessId, limit),
    (current: { bookings: unknown[]; totalCount: number } | undefined) => {
      if (!current) return current;
      const filtered = filterOut(current.bookings);
      return {
        bookings: filtered,
        totalCount: Math.max(0, current.totalCount - (current.bookings.length - filtered.length)),
      };
    },
    { revalidate: false }
  );

  // Remove from due
  mutate(
    swrKeys.dueBookings(businessId, limit),
    (current: { bookings: unknown[]; totalCount: number } | undefined) => {
      if (!current) return current;
      const filtered = filterOut(current.bookings);
      return {
        bookings: filtered,
        totalCount: Math.max(0, current.totalCount - (current.bookings.length - filtered.length)),
      };
    },
    { revalidate: false }
  );

  // Also update dashboard cache
  mutate(
    dashboardKey(businessId),
    (current: DashboardDataShape | undefined) => {
      if (!current) return current;

      return {
        ...current,
        upcoming: {
          bookings: filterOut(current.upcoming?.bookings ?? []) as DashboardDataShape['upcoming']['bookings'],
          totalCount: Math.max(0, (current.upcoming?.totalCount ?? 0) - 1),
        },
        due: {
          bookings: filterOut(current.due?.bookings ?? []) as DashboardDataShape['due']['bookings'],
          totalCount: Math.max(0, (current.due?.totalCount ?? 0) - 1),
        },
      };
    },
    { revalidate: false }
  );
}

/**
 * Optimistically update a booking status in all caches.
 */
export function optimisticUpdateBookingStatus(
  businessId: string,
  bookingId: string,
  newStatus: string,
  limit: number = 5
) {
  markOptimisticUpdate(); // Mark timestamp to prevent redundant refetches
  const updateStatus = (booking: any) => {
    if (booking.id === bookingId) {
      return { ...booking, status: newStatus };
    }
    return booking;
  };

  // Update in upcoming bookings
  mutate(
    swrKeys.upcomingBookings(businessId, limit),
    (current: { bookings: unknown[]; totalCount: number } | undefined) => {
      if (!current) return current;
      return {
        ...current,
        bookings: current.bookings.map(updateStatus),
      };
    },
    { revalidate: false }
  );

  // Update in due bookings
  mutate(
    swrKeys.dueBookings(businessId, limit),
    (current: { bookings: unknown[]; totalCount: number } | undefined) => {
      if (!current) return current;
      return {
        ...current,
        bookings: current.bookings.map(updateStatus),
      };
    },
    { revalidate: false }
  );

  // Also update dashboard cache
  mutate(
    dashboardKey(businessId),
    (current: DashboardDataShape | undefined) => {
      if (!current) return current;

      return {
        ...current,
        upcoming: {
          ...current.upcoming,
          bookings: (current.upcoming?.bookings ?? []).map(updateStatus),
        },
        due: {
          ...current.due,
          bookings: (current.due?.bookings ?? []).map(updateStatus),
        },
      };
    },
    { revalidate: false }
  );
}

/**
 * Optimistically update earnings after a payment.
 */
export function optimisticUpdateEarnings(
  businessId: string,
  amount: number,
  method: 'cash' | 'card' | 'transfer'
) {
  markOptimisticUpdate(); // Mark timestamp to prevent redundant refetches
  // Update individual earnings hook cache
  mutate(
    swrKeys.todayEarnings(businessId),
    (current: { total: number; cash: number; card: number } | undefined) => {
      if (!current) return { total: amount, cash: 0, card: 0, [method]: amount };
      return {
        ...current,
        total: current.total + amount,
        [method]: (current[method as keyof typeof current] as number || 0) + amount,
      };
    },
    { revalidate: false }
  );

  // Also update dashboard cache
  mutate(
    dashboardKey(businessId),
    (current: DashboardDataShape | undefined) => {
      if (!current) return current;

      const currentEarnings = current.earnings ?? { total: 0, cash: 0, card: 0 };

      return {
        ...current,
        earnings: {
          ...currentEarnings,
          total: currentEarnings.total + amount,
          [method]: (currentEarnings[method as keyof typeof currentEarnings] as number || 0) + amount,
        },
      };
    },
    { revalidate: false }
  );
}

/**
 * Optimistically add a transaction to the sales summary.
 */
export function optimisticAddTransaction(
  businessId: string,
  transaction: {
    id: string;
    amount_aed: number;
    method: string;
    status: string;
    created_at: string;
    booking?: { customer?: { name: string } | null } | null;
  },
  range: 'today' | 'week' | 'month' = 'today'
) {
  const today = new Date().toISOString().split('T')[0];
  const key = swrKeys.salesSummary(businessId, range, today);

  mutate(
    key,
    (current: any) => {
      if (!current) return current;

      const amount = Number(transaction.amount_aed);

      return {
        ...current,
        totalRevenue: current.totalRevenue + amount,
        transactionCount: current.transactionCount + 1,
        [`${transaction.method}Payments`]:
          (current[`${transaction.method}Payments`] || 0) + amount,
        transactions: [transaction, ...current.transactions.slice(0, 19)],
      };
    },
    { revalidate: false }
  );
}

// ============================================================================
// Customer Optimistic Updates
// ============================================================================

/**
 * Optimistically add a customer to the customers list.
 */
export function optimisticAddCustomer(
  businessId: string,
  customer: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    [key: string]: unknown;
  }
) {
  mutate(
    swrKeys.customers(businessId),
    (current: unknown[] | undefined) => {
      if (!current) return [customer];
      return [customer, ...current];
    },
    { revalidate: false }
  );
}

/**
 * Optimistically update a customer in the customers list.
 */
export function optimisticUpdateCustomer(
  businessId: string,
  customerId: string,
  updates: Record<string, unknown>
) {
  mutate(
    swrKeys.customers(businessId),
    (current: unknown[] | undefined) => {
      if (!current) return current;
      return current.map((c: any) =>
        c.id === customerId ? { ...c, ...updates } : c
      );
    },
    { revalidate: false }
  );
}

/**
 * Optimistically remove a customer from the customers list.
 */
export function optimisticRemoveCustomer(
  businessId: string,
  customerId: string
) {
  mutate(
    swrKeys.customers(businessId),
    (current: unknown[] | undefined) => {
      if (!current) return current;
      return current.filter((c: any) => c.id !== customerId);
    },
    { revalidate: false }
  );
}

// ============================================================================
// Service Optimistic Updates
// ============================================================================

/**
 * Optimistically add a service to the services list.
 */
export function optimisticAddService(
  businessId: string,
  service: {
    id: string;
    name: string;
    active?: boolean;
    [key: string]: unknown;
  }
) {
  // Add to active services if service is active
  if (service.active !== false) {
    mutate(
      swrKeys.services(businessId),
      (current: unknown[] | undefined) => {
        if (!current) return [service];
        return [...current, service].sort((a: any, b: any) =>
          a.name.localeCompare(b.name)
        );
      },
      { revalidate: false }
    );
  }

  // Always add to all services
  mutate(
    swrKeys.allServices(businessId),
    (current: unknown[] | undefined) => {
      if (!current) return [service];
      return [...current, service].sort((a: any, b: any) =>
        a.name.localeCompare(b.name)
      );
    },
    { revalidate: false }
  );
}

/**
 * Optimistically update a service in the services lists.
 */
export function optimisticUpdateService(
  businessId: string,
  serviceId: string,
  updates: Record<string, unknown>
) {
  const updateFn = (current: unknown[] | undefined) => {
    if (!current) return current;
    return current.map((s: any) =>
      s.id === serviceId ? { ...s, ...updates } : s
    );
  };

  mutate(swrKeys.services(businessId), updateFn, { revalidate: false });
  mutate(swrKeys.allServices(businessId), updateFn, { revalidate: false });
}

/**
 * Optimistically remove a service from the services lists.
 */
export function optimisticRemoveService(
  businessId: string,
  serviceId: string
) {
  const filterFn = (current: unknown[] | undefined) => {
    if (!current) return current;
    return current.filter((s: any) => s.id !== serviceId);
  };

  mutate(swrKeys.services(businessId), filterFn, { revalidate: false });
  mutate(swrKeys.allServices(businessId), filterFn, { revalidate: false });
}

// ============================================================================
// Staff Optimistic Updates
// ============================================================================

/**
 * Optimistically add a staff member to the staff lists.
 */
export function optimisticAddStaff(
  businessId: string,
  staff: {
    id: string;
    display_name: string;
    active?: boolean;
    [key: string]: unknown;
  }
) {
  // Add to active staff if staff is active
  if (staff.active !== false) {
    mutate(
      swrKeys.staff(businessId),
      (current: unknown[] | undefined) => {
        if (!current) return [staff];
        return [...current, staff].sort((a: any, b: any) =>
          a.display_name.localeCompare(b.display_name)
        );
      },
      { revalidate: false }
    );
  }

  // Always add to all staff
  mutate(
    swrKeys.allStaff(businessId),
    (current: unknown[] | undefined) => {
      if (!current) return [staff];
      return [...current, staff].sort((a: any, b: any) =>
        a.display_name.localeCompare(b.display_name)
      );
    },
    { revalidate: false }
  );
}

/**
 * Optimistically update a staff member in the staff lists.
 */
export function optimisticUpdateStaff(
  businessId: string,
  staffId: string,
  updates: Record<string, unknown>
) {
  const updateFn = (current: unknown[] | undefined) => {
    if (!current) return current;
    return current.map((s: any) =>
      s.id === staffId ? { ...s, ...updates } : s
    );
  };

  mutate(swrKeys.staff(businessId), updateFn, { revalidate: false });
  mutate(swrKeys.allStaff(businessId), updateFn, { revalidate: false });
}

/**
 * Optimistically remove a staff member from the staff lists.
 */
export function optimisticRemoveStaff(
  businessId: string,
  staffId: string
) {
  const filterFn = (current: unknown[] | undefined) => {
    if (!current) return current;
    return current.filter((s: any) => s.id !== staffId);
  };

  mutate(swrKeys.staff(businessId), filterFn, { revalidate: false });
  mutate(swrKeys.allStaff(businessId), filterFn, { revalidate: false });
}

// ============================================================================
// Inventory Optimistic Updates
// ============================================================================

/**
 * Optimistically update inventory quantity.
 * Pass positive delta to increase, negative to decrease.
 */
export function optimisticUpdateInventory(
  businessId: string,
  productId: string,
  quantityDelta: number
) {
  // Invalidate all inventory keys for this business with optimistic update
  mutate(
    (key) => {
      if (typeof key === 'string') {
        return key.startsWith(`inventory-${businessId}`);
      }
      if (Array.isArray(key)) {
        return key[0] === 'inventory' && key.includes(businessId);
      }
      return false;
    },
    (current: unknown[] | undefined) => {
      if (!current) return current;
      return current.map((item: any) =>
        item.id === productId
          ? { ...item, quantity: Math.max(0, (item.quantity || 0) + quantityDelta) }
          : item
      );
    },
    { revalidate: false }
  );
}

// ============================================================================
// Alert Optimistic Updates
// ============================================================================

/**
 * Optimistically dismiss an alert.
 */
export function optimisticDismissAlert(
  businessId: string,
  alertId: string
) {
  mutate(
    swrKeys.alerts(businessId),
    (current: unknown[] | undefined) => {
      if (!current) return current;
      return current.filter((a: any) => a.id !== alertId);
    },
    { revalidate: false }
  );
}

// ============================================================================
// High-Level Mutation Actions
// ============================================================================

/**
 * After creating a booking, update all relevant caches.
 */
export function afterBookingCreated(
  businessId: string,
  booking: unknown
) {
  // Optimistic add to upcoming
  optimisticAddBooking(businessId, booking);

  // Schedule revalidation
  batchInvalidate(businessId, ['bookings', 'customers', 'inbox']);
}

/**
 * After completing a payment, update all relevant caches.
 */
export function afterPaymentCompleted(
  businessId: string,
  payment: {
    id: string;
    amount_aed: number;
    method: 'cash' | 'card' | 'transfer';
    status: string;
    created_at: string;
    booking_id?: string;
    booking?: { customer?: { name: string } | null } | null;
  }
) {
  // Optimistic update earnings
  optimisticUpdateEarnings(businessId, Number(payment.amount_aed), payment.method);

  // Optimistic add to sales
  optimisticAddTransaction(businessId, {
    id: payment.id,
    amount_aed: payment.amount_aed,
    method: payment.method,
    status: payment.status,
    created_at: payment.created_at,
    booking: payment.booking,
  });

  // Schedule revalidation for all financial data
  batchInvalidate(businessId, ['earnings', 'salesSummary', 'payments', 'bookings']);
}

/**
 * After marking a booking as paid, update all relevant caches.
 */
export function afterBookingMarkedPaid(
  businessId: string,
  bookingId: string,
  payment: {
    id: string;
    amount_aed: number;
    method: 'cash' | 'card' | 'transfer';
    status: string;
    created_at: string;
    booking?: { customer?: { name: string } | null } | null;
  }
) {
  // Update booking status optimistically
  optimisticUpdateBookingStatus(businessId, bookingId, 'confirmed_paid');

  // Handle payment updates
  afterPaymentCompleted(businessId, payment);
}

/**
 * After updating a booking status (complete, cancel, no-show, etc.)
 */
export function afterBookingStatusUpdated(
  businessId: string,
  bookingId: string,
  newStatus: string
) {
  // Optimistic update
  optimisticUpdateBookingStatus(businessId, bookingId, newStatus);

  // If completed/cancelled/no-show, remove from lists
  if (['completed', 'confirmed_paid', 'cancelled', 'no_show'].includes(newStatus)) {
    optimisticRemoveBooking(businessId, bookingId);
  }

  // Schedule revalidation
  batchInvalidate(businessId, ['bookings']);
}

/**
 * After creating a customer, update caches.
 */
export function afterCustomerCreated(
  businessId: string,
  customer: { id: string; name: string; phone?: string; email?: string; [key: string]: unknown }
) {
  optimisticAddCustomer(businessId, customer);
  batchInvalidate(businessId, ['customers']);
}

/**
 * After updating a customer, update caches.
 */
export function afterCustomerUpdated(
  businessId: string,
  customerId: string,
  updates: Record<string, unknown>
) {
  optimisticUpdateCustomer(businessId, customerId, updates);
  batchInvalidate(businessId, ['customers']);
}

/**
 * After creating a service, update caches.
 */
export function afterServiceCreated(
  businessId: string,
  service: { id: string; name: string; active?: boolean; [key: string]: unknown }
) {
  optimisticAddService(businessId, service);
  batchInvalidate(businessId, ['services']);
}

/**
 * After updating a service, update caches.
 */
export function afterServiceUpdated(
  businessId: string,
  serviceId: string,
  updates: Record<string, unknown>
) {
  optimisticUpdateService(businessId, serviceId, updates);
  batchInvalidate(businessId, ['services']);
}

/**
 * After creating a staff member, update caches.
 */
export function afterStaffCreated(
  businessId: string,
  staff: { id: string; display_name: string; active?: boolean; [key: string]: unknown }
) {
  optimisticAddStaff(businessId, staff);
  batchInvalidate(businessId, ['staff']);
}

/**
 * After updating a staff member, update caches.
 */
export function afterStaffUpdated(
  businessId: string,
  staffId: string,
  updates: Record<string, unknown>
) {
  optimisticUpdateStaff(businessId, staffId, updates);
  batchInvalidate(businessId, ['staff']);
}

/**
 * After dismissing an alert, update caches.
 */
export function afterAlertDismissed(
  businessId: string,
  alertId: string
) {
  optimisticDismissAlert(businessId, alertId);
  batchInvalidate(businessId, ['alerts']);
}
